// Shared helpers for the redesigned Playground (list + detail flow).
// Kept React-free (only the TaskType enum is imported) so list/detail/output
// components can pull only what they need.

import { TaskType } from '@/shared/domain/taskTypes';
export const countWords = (s) => {
    const str = String(s || '').trim();
    if (!str) return 0;
    return str.split(/\s+/).filter(Boolean).length;
};

export const countChars = (s) => String(s || '').length;

// Client-side token estimate for the `Tokens: N in/out` footers (Figma data
// rows). Inference responses carry no usage info, so we approximate with the
// common ~4 chars/token heuristic; whitespace-trimmed, floor of 0.
export const estimateTokens = (s) => {
    const str = String(s || '').trim();
    if (!str) return 0;
    return Math.max(1, Math.round(str.length / 4));
};

// Collapsed data-row cells clamp at 163px. Subpixel rounding can report 1px
// of false overflow, so treat that as "fits".
export const COLLAPSED_OVERFLOW_SLACK_PX = 1;

export const elementOverflowsVertically = (el) =>
    !!el && el.scrollHeight > el.clientHeight + COLLAPSED_OVERFLOW_SLACK_PX;

// Expand is only useful when the example textarea or a clamped output cell
// is actually clipped — or when the row is already expanded (so Collapse
// stays available).
export const rowNeedsExpandButton = ({ expanded, textarea, outputEls = [] }) => {
    if (expanded) return true;
    if (elementOverflowsVertically(textarea)) return true;
    return outputEls.some(elementOverflowsVertically);
};

// Resolve a model id to a friendly label across the three model registries the
// Playground knows about (OpenAI raw names, custom SpecificAI models keyed by id,
// Bedrock Anthropic models keyed by model_profile_id). Returns the raw id if the
// model is not found, so the toolbar still shows *something* during the brief
// window while registries are loading.
export const getModelDisplayName = (
    modelId,
    { openAIModelNames = [], optuneModels = [], bedrockAnthropicModels = [] } = {}
) => {
    if (!modelId) return '';
    if (openAIModelNames.includes(modelId)) return modelId;
    const optune = optuneModels.find((m) => m.id === modelId);
    if (optune) return optune.display_name || modelId;
    const bedrock = bedrockAnthropicModels.find((m) => m.model_profile_id === modelId);
    if (bedrock) return bedrock.model_name || bedrock.model_profile_id || modelId;
    return modelId;
};

// Returns `"openai" | "bedrock-anthropic" | null`. Null means the id belongs to a
// SpecificAI custom model (or is unknown).
export const getModelProvider = (
    modelId,
    { openAIModelNames = [], bedrockAnthropicModels = [] } = {}
) => {
    if (!modelId) return null;
    if (openAIModelNames.includes(modelId)) return 'openai';
    if (bedrockAnthropicModels.some((m) => m.model_profile_id === modelId)) return 'bedrock-anthropic';
    return null;
};

// Friendly label for a backend ``ModelProvider`` enum value. Used by the
// Playground model picker's compact card to summarise where the selected
// model is hosted (e.g. "AWS Bedrock", "Anthropic", "SpecificAI fine-tuned").
// Anything unrecognised is returned verbatim so a new provider added on the
// backend still surfaces something readable without a frontend change.
const PROVIDER_DISPLAY_LABELS = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    deepseek: 'DeepSeek',
    jev: 'TypeSafe AI',
    bedrock: 'AWS Bedrock',
    azure: 'Azure AI',
    vertex: 'Google Vertex AI',
    specificai: 'SpecificAI',
};
export const getProviderDisplayLabel = (provider) => {
    if (!provider) return '';
    return PROVIDER_DISPLAY_LABELS[provider] || provider;
};

export const isOptuneModel = (modelId, optuneModels = []) =>
    !!modelId && optuneModels.some((m) => m.id === modelId);

/** SpecificAI fine-tuned models on non-generative tasks run on raw input —
 *  their column shows the "no prompt needed" note instead of prompt fields. */
export const isNoPromptSlot = (slot, isGenerative) =>
    !isGenerative && slot?.provider === 'specificai';

/** Cluster-wide GPU control belongs on every generative Playground.
 *  Do not wait for a SpecificAI slot or a live session — that hid Start GPU
 *  after a hard refresh (idle hook + slots not seeded yet). */
export const shouldShowGpuControl = (isGenerative) => !!isGenerative;

/** "Run all" waits for the GPU session only when a SpecificAI model would
 *  actually run on it: generative tasks are served by the vLLM Playground
 *  deployment that Start GPU scales up. Classification / NER fine-tuned
 *  models run on Triton and never need (or get) a Start GPU button, so
 *  gating them here left Run all permanently disabled. */
export const isGpuRunBlocked = ({ isGenerative, hasSpecificSlot, isGpuRunning }) =>
    !!isGenerative && !!hasSpecificSlot && !isGpuRunning;

/**
 * Label for the task's configured teacher / commercial model.
 * The usecase stores the UI name as ``teacher_model_display`` (API param
 * ``teacher_display``); ``teacher_model`` is the id, which for OpenAI is
 * already the same string shown in Model Configuration.
 */
export const taskTeacherDisplayName = (usecase) => {
    if (!usecase) return '';
    const name =
        usecase.teacher_model_display ||
        usecase.teacher_display ||
        usecase.teacher_model ||
        '';
    return typeof name === 'string' ? name.trim() : String(name);
};

/**
 * Default Playground column(s) when opening a task. Prefer the latest
 * SpecificAI version (``usecase-models`` is sorted newest-first); fall back
 * to the teacher only when the task has no trained SpecificAI model.
 */
export const defaultPlaygroundSlots = ({
    specificAIModels = [],
    teacherModel,
    teacherProvider,
    teacherDisplay,
} = {}) => {
    const newestSpecific = specificAIModels[0];
    if (newestSpecific) {
        return [{
            provider: 'specificai',
            model: newestSpecific.value,
            displayName: newestSpecific.label,
        }];
    }
    if (teacherModel) {
        return [{
            provider: teacherProvider || 'openai',
            model: teacherModel,
            displayName: teacherDisplay || teacherModel,
        }];
    }
    return [];
};

/** Keep prompt-capable slots contiguous (and first) so the shared prompt
 *  panel / "Split prompts" can span every model that uses a prompt. Stable
 *  within each group. */
export const orderSlotsByPromptNeed = (slotList, isGenerative) => {
    const withPrompt = [];
    const withoutPrompt = [];
    for (const slot of slotList || []) {
        if (isNoPromptSlot(slot, isGenerative)) withoutPrompt.push(slot);
        else withPrompt.push(slot);
    }
    return [...withPrompt, ...withoutPrompt];
};

// NER tasks need a Response Parser to extract entities from a commercial
// model's raw text output. SpecificAI models for these tasks return entities
// directly (no parser needed). When the parser is missing for a commercial
// side, the backend ``/inference-commercial`` route returns just
// ``{raw_result}`` (see ``backend/app/routes/playground_route.py`` — the
// ``parser_function is None`` short-circuit), so the UI has nothing to
// highlight on the example. In that case we render an indicative message
// instead of the raw LLM text. This predicate centralises the rule so the
// example detail card and the table cell stay in sync.
export const needsParserMessage = ({
    taskType,
    model,
    optuneModels,
    hasParserFunction,
}) => {
    if (!model) return false;
    if (hasParserFunction) return false;
    if (isOptuneModel(model, optuneModels)) return false;
    const tt = (taskType || '').toLowerCase();
    return (
        tt === TaskType.NER.toLowerCase() ||
        tt === TaskType.NER_WITH_RELATIONS.toLowerCase()
    );
};

// Best-effort plain-text extraction from a single inference result. Used by the
// list view to show a short snippet, and by the detail view's word/char footer.
export const extractCompletionText = (completion) => {
    if (!completion) return '';
    const parsed = completion.parsed_result;
    if (typeof parsed?.response === 'string') return parsed.response;
    if (Array.isArray(parsed?.entities)) {
        return completion.originalText || completion.raw_result || '';
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.join(', ');
    }
    return completion.raw_result || '';
};

export const playgroundOutputKey = (rowId, slotId) => `${rowId}|${slotId}`;

export const ALREADY_IN_BENCHMARK_TOAST =
    'This example already exists in the benchmark.';

export const benchmarkSelectionBlockedReason = (exampleText, existingExamples) =>
    existingExamples.has(exampleText) ? ALREADY_IN_BENCHMARK_TOAST : null;

export const rowIdFromPlaygroundOutputKey = (key) => {
    const sep = String(key).indexOf('|');
    return sep === -1 ? String(key) : String(key).slice(0, sep);
};

/**
 * Benchmark save keeps at most one output per example. Clicking the selected
 * output deselects it; clicking another output on the same row replaces it.
 */
export const toggleBenchmarkOutputSelection = (selected, rowId, slotId) => {
    const key = playgroundOutputKey(rowId, slotId);
    const prefix = `${rowId}|`;
    const next = new Set();
    for (const existing of selected) {
        if (!existing.startsWith(prefix)) next.add(existing);
    }
    if (!selected.has(key)) next.add(key);
    return next;
};

export const uniqueExampleCountFromOutputKeys = (doneOutputKeys) => {
    const rowIds = new Set();
    for (const key of doneOutputKeys) {
        rowIds.add(rowIdFromPlaygroundOutputKey(key));
    }
    return rowIds.size;
};

/**
 * "Select All" for benchmark save: one completed output per example. Keeps an
 * existing choice on a row; otherwise picks the first completed slot.
 */
export const selectOneBenchmarkOutputPerExample = (
    doneOutputKeys,
    selected = new Set()
) => {
    const firstByRow = new Map();
    const chosenByRow = new Map();
    for (const key of doneOutputKeys) {
        const rowId = rowIdFromPlaygroundOutputKey(key);
        if (!firstByRow.has(rowId)) firstByRow.set(rowId, key);
        if (selected.has(key)) chosenByRow.set(rowId, key);
    }
    const next = new Set();
    for (const [rowId, first] of firstByRow) {
        next.add(chosenByRow.get(rowId) || first);
    }
    return next;
};

export const filterOutputKeysNotInBenchmark = (
    doneOutputKeys,
    exampleTextByRowId,
    existingExamples
) =>
    doneOutputKeys.filter((key) => {
        const rowId = rowIdFromPlaygroundOutputKey(key);
        const text = exampleTextByRowId.get(rowId) ?? '';
        return !existingExamples.has(text);
    });

/**
 * Dispatch a Playground "Run all".
 *
 * Commercial models hit external providers and can run concurrently, so we fire
 * them immediately. SpecificAI models share a single in-pod GPU whose router
 * serializes generation across bases (it sleeps one base and wakes another
 * between requests); dispatching them concurrently buys no throughput and only
 * forces a sleep/wake on every interleaved request. So we run the SpecificAI
 * slots one at a time — each awaits the previous — keeping each base's requests
 * grouped (one wake per base, not one per request).
 *
 * ``runSlot(slot)`` performs one slot's inference and returns a promise that
 * settles when it finishes; it must handle its own errors, as the SpecificAI
 * chain relies on it not rejecting. Returns the SpecificAI chain promise
 * (resolves once every SpecificAI slot has run); commercial slots are not
 * awaited.
 */
export const dispatchPlaygroundSlots = (slots = [], runSlot) => {
    const isSpecificAI = (slot) => slot?.provider === 'specificai';
    for (const slot of slots) {
        if (!isSpecificAI(slot)) runSlot(slot);
    }
    return slots
        .filter(isSpecificAI)
        .reduce((chain, slot) => chain.then(() => runSlot(slot)), Promise.resolve());
};
